import { InAppNotification } from 'types/InAppNotification';
import { useInAppNotificationClick } from 'components/in-app-notifications/InAppNotifications.hooks';
import { Avatar, Icon } from '@kontentino/ui';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons/faTriangleExclamation';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import dayjs from 'dayjs';
import DateUtils from 'app/utils/date';
import {
  getNotificationSeverity,
  notificationMessageHTML,
} from 'components/in-app-notifications/notificationMessage';
import clsx from 'clsx';

interface Props {
  notification: InAppNotification;
}

function NotificationItem({ notification }: Props) {
  const openNotification = useInAppNotificationClick(notification);

  const isUnread = !notification.is_read;
  const severity = getNotificationSeverity(notification);

  return (
    <div
      className={clsx(
        'tw-flex tw-cursor-pointer tw-items-start tw-gap-2 tw-rounded tw-p-3',
        {
          'tw-bg-warning-10 hover:tw-bg-warning-20':
            severity === 'info' && isUnread,
          'hover:tw-bg-warning-10': severity === 'info' && !isUnread,
          'tw-bg-danger-10 hover:tw-bg-danger-20':
            severity === 'urgent' && isUnread,
          'hover:tw-bg-danger-10': severity === 'urgent' && !isUnread,
          'tw-bg-success-10 hover:tw-bg-success-20':
            severity === 'success' && isUnread,
          'hover:tw-bg-success-10': severity === 'success' && !isUnread,
          'tw-bg-primary-10 hover:tw-bg-primary-20':
            severity === 'default' && isUnread,
          'hover:tw-bg-grayscale-5': severity === 'default' && !isUnread,
        },
      )}
      onClick={openNotification}
      data-cy="notifications_item"
      data-testid="notification-item"
      data-is-unread={isUnread}
    >
      {severity !== 'default' ? (
        <div
          className={clsx(
            'tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full',
            {
              'tw-bg-warning-20 tw-text-warning-140': severity === 'info',
              'tw-bg-danger-20 tw-text-danger-140': severity === 'urgent',
              'tw-bg-success-20 tw-text-success-140': severity === 'success',
            },
          )}
          data-testid="notifications-tooltip-item_urgent-icon"
          data-severity={severity}
        >
          <Icon
            icon={
              severity === 'success' ? faCircleCheck : faTriangleExclamation
            }
            size="sm"
          />
        </div>
      ) : (
        <Avatar
          name={notification.author}
          src={notification.picture}
          size={32}
        />
      )}
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
        <div
          className={clsx('tw-text-sm tw-text-grayscale-180', {
            'tw-font-semibold': isUnread,
          })}
          dangerouslySetInnerHTML={{
            __html: notificationMessageHTML({
              type: notification.type,
              author: notification.author ?? '',
              pageName: notification.page_name ?? '',
              pageCount: notification.page_count ?? 1,
              pages: notification.pages,
            }),
          }}
        />
        <span
          className={clsx('tw-text-xs', {
            'tw-font-medium tw-text-primary-100': isUnread,
            'tw-text-grayscale-90': !isUnread,
          })}
        >
          {DateUtils.toCompactRelative(dayjs.utc(notification.created_at))}
        </span>
      </div>
    </div>
  );
}

export default NotificationItem;
